import { useState, useEffect, useRef } from "react";
import { RiCloseLargeFill, RiDeleteBinLine, RiAddFill } from "react-icons/ri";
import DynamicSelect from "../../DynamicSelect";
import { toast } from "react-toastify";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { addToNicuOutbox, saveNicuLocal, generateClientId } from "../../../services/nicuOutbox";
import { NicuPrintLayout } from "../../NicuPrintLayout";
import NicuAgentModal from "../NicuConfig/NicuAgent/NicuAgentModal";
const API_BASE_URL = "https://api.muktihospital.com/api";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const NicuBillModal = ({ isOpen, onClose, billId, initialData, onSave }) => {
  const [formData, setFormData] = useState({
    specialNo: "",
    regNo: "",
    patientName: "",
    consultantName: "",
    bedNo: "",
    gender: "",
    age: "",
    admissionDate: "",
    authorizedBy:"",
    billingDate: new Date().toISOString().split("T")[0],
    refer: "",
    referAmount: "",
    billNumber: "",
  });
  const [authorizedByOptions, setAuthorizedByOptions] = useState([]);
  const [selectedBillId, setSelectedBillId] = useState(billId || null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [serviceCharge, setServiceCharge] = useState("0");
  const [serviceChargePc, setServiceChargePc] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [discountBy, setDiscountBy] = useState(null);
  const [paidAmount, setPaidAmount] = useState("0");
  const [advanceInfo, setAdvanceInfo] = useState(null);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [billResponse, setBillResponse] = useState({
    billAmount: 0,
    serviceChargePc: 0,
    serviceCharge: 0,
    totalAmount: 0,
    discountPc: 0,
    discount: 0,
    netAmount: 0,
    paidAmount: 0,
    payableAmount: 0,
  });
  const [billNumberSearch, setBillNumberSearch] = useState("");
  const [billNumberOptions, setBillNumberOptions] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [billExists, setBillExists] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [discountUsers, setDiscountUsers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("https://api.muktihospital.com/api/nicu/discount-user", {
          headers: { "x-api-key": API_KEY },
        });
        const users = res.data.users || [];
        setDiscountUsers(users);
        saveToCache("nicu_bill_discount_users", users);
      } catch (error) {
        console.error("Failed to load discount users:", error);
        if (error.code === "ERR_NETWORK" || !navigator.onLine) {
          const cached = await readFromCache("nicu_bill_discount_users");
          if (cached && Array.isArray(cached)) setDiscountUsers(cached);
        }
      }
    })();
  }, []);
  const searchRef = useRef(null);
  const serviceBodyRef = useRef(null);

  const calculateBillAmounts = (billData) => {
    let billAmount = 0;
    if (billData.serviceItems && Array.isArray(billData.serviceItems)) {
      billAmount = billData.serviceItems.reduce(
        (sum, item) => sum + (item.unitPrice * item.totalUnit),
        0
      );
    }

    const serviceChargePcVal = parseFloat(billData.serviceChargePc || 0);
    const serviceChargeVal =
      serviceChargePcVal > 0
        ? (billAmount * serviceChargePcVal) / 100
        : parseFloat(billData.serviceCharge || 0);
    const totalAmount = billAmount + serviceChargeVal;

    const discountVal = parseFloat(billData.discount || 0);
    const netAmount = totalAmount - discountVal;

    const paidAmount = parseFloat(billData.paidAmount || 0);
    const payableAmount = netAmount - paidAmount;

    return {
      billAmount: Math.round(billAmount),
      serviceChargePc: serviceChargePcVal,
      serviceCharge: Math.round(serviceChargeVal),
      totalAmount: Math.round(totalAmount),
      discount: Math.round(discountVal),
      netAmount: Math.round(netAmount),
      paidAmount: Math.round(paidAmount),
      payableAmount: Math.round(payableAmount),
    };
  };

  useEffect(() => {
    const calculatedAmounts = calculateBillAmounts({
      serviceItems: tableData,
      serviceCharge,
      serviceChargePc,
      discount,
      paidAmount: parseFloat(paidAmount) || 0,
    });
    setBillResponse((prev) => ({
      ...prev,
      ...calculatedAmounts,
    }));
  }, [tableData, serviceCharge, serviceChargePc, discount, paidAmount]);

useEffect(() => {
    if (!isOpen) return;
    
    const fetchData = async () => {
      try {
        const [admissionsResp, servicesResp, agentsResp, billsResp, authoritiesResp, doctorsResp] = await Promise.all([
          axios.get(`${API_BASE_URL}/nicu/admission`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/nicu/service`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/nicu/agent`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/nicu/nicu-bills`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/authorities`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } })
        ]);

        const admissionsData = admissionsResp.data.data?.admissions || [];
        setPatients(
          admissionsData
            .filter((admission) => admission.status === "Admitted")
            .map((admission) => ({
              value: admission.id,
              label: admission.patientName || "Unknown",
              specialNo: admission.specialNo || "",
              regNo: admission.regNo || "",
              sex: admission.sex || "",
              age: admission.age || "",
              bedNo: admission.bedNo || "",
              admissionDate: admission.admissionDate ? admission.admissionDate.split("T")[0] : "",
              consultantName: admission.consultantName || "",
              agentName: admission.agentName || "",
              phoneNumber: admission.phoneNumber || admission.relativeMobileNumber || "",
              patientId: admission.patientId || "",
            }))
        );

        const servicesData = servicesResp.data?.services || [];
        setServices(
          servicesData.map((service) => ({
            value: service.id,
            label: service.serviceName || "Unknown",
            particulars: service.particulars || "",
            providedBy: service.providedBy || "",
            unitPrice: service.unitPrice || 0,
          }))
        );

        const agentsData = agentsResp.data?.agents || [];
        setAgents(
          agentsData.map((agent) => ({
            value: agent.id,
            label: agent.agentName || "Unknown",
          }))
        );

        const billsData = billsResp.data?.bills || billsResp.data || [];
        setAllBills(billsData);
        setBillNumberOptions(billsData.map((bill) => bill.billNumber || ""));

        const authorityList = authoritiesResp.data || [];
        setAuthorizedByOptions(
          authorityList.map((auth) => ({
            value: auth.name,
            label: auth.name,
          }))
        );

        const doctorsData = Array.isArray(doctorsResp.data) ? doctorsResp.data : (doctorsResp.data.data || []);
        setConsultants(
          doctorsData.map((d) => {
             let name = d.name || "Unknown";
             if(d.translations) {
                 try {
                     const t = typeof d.translations === 'string' ? JSON.parse(d.translations) : d.translations;
                     name = t?.en?.name || name;
                 } catch(e){}
             }
             return { value: name, label: name }; 
          })
        );

        // Cache raw responses so the whole form works offline.
        saveToCache("nicu_bill_form_data", {
          admissions: admissionsData,
          services: servicesData,
          agents: agentsData,
          bills: billsData,
          authorities: authorityList,
          doctors: doctorsData,
        });

      } catch (error) {
        console.error("Error fetching initial data:", error);
        if (error.code === "ERR_NETWORK" || !navigator.onLine) {
          const cached = await readFromCache("nicu_bill_form_data");
          if (cached) {
            const admissionsData = cached.admissions || [];
            setPatients(
              admissionsData
                .filter((admission) => admission.status === "Admitted")
                .map((admission) => ({
                  value: admission.id,
                  label: admission.patientName || "Unknown",
                  specialNo: admission.specialNo || "",
                  regNo: admission.regNo || "",
                  sex: admission.sex || "",
                  age: admission.age || "",
                  bedNo: admission.bedNo || "",
                  admissionDate: admission.admissionDate ? admission.admissionDate.split("T")[0] : "",
                  consultantName: admission.consultantName || "",
                  agentName: admission.agentName || "",
                  phoneNumber: admission.phoneNumber || admission.relativeMobileNumber || "",
                  patientId: admission.patientId || "",
                }))
            );
            setServices(
              (cached.services || []).map((service) => ({
                value: service.id,
                label: service.serviceName || "Unknown",
                particulars: service.particulars || "",
                providedBy: service.providedBy || "",
                unitPrice: service.unitPrice || 0,
              }))
            );
            setAgents(
              (cached.agents || []).map((agent) => ({
                value: agent.id,
                label: agent.agentName || "Unknown",
              }))
            );
            const billsData = cached.bills || [];
            setAllBills(billsData);
            setBillNumberOptions(billsData.map((bill) => bill.billNumber || ""));
            setAuthorizedByOptions(
              (cached.authorities || []).map((auth) => ({
                value: auth.name,
                label: auth.name,
              }))
            );
            setConsultants(
              (cached.doctors || []).map((d) => {
                let name = d.name || "Unknown";
                if (d.translations) {
                  try {
                    const t = typeof d.translations === 'string' ? JSON.parse(d.translations) : d.translations;
                    name = t?.en?.name || name;
                  } catch (e) {}
                }
                return { value: name, label: name };
              })
            );
            return;
          }
        }
        toast.error("Failed to fetch initial data.", { autoClose: 2000 });
        setPatients([]);
        setServices([]);
        setAgents([]);
        setAllBills([]);
        setBillNumberOptions([]);
        setAuthorizedByOptions([]);
      }
    };
    fetchData();
  }, [isOpen]);

useEffect(() => {
  if (isOpen) {
    if (billId || initialData) {
      const loadBill = async () => {
        setIsLoading(true);
        try {
          const response = initialData
            ? { data: initialData }
            : await axios.get(`https://api.muktihospital.com/api/nicu/nicu-bills/${billId}`, {
                headers: {
                  "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
                },
              });

          const bill = response.data.bill || response.data;

          console.log("DEBUG: Full Fetched Bill Data:", bill);
          console.log("DEBUG: Consultant Name Value:", bill.consultantName);

          setSelectedBillId(bill.id || billId);
          const agent = agents.find((a) => a.value === bill.refer);
          
          setFormData({
            specialNo: bill.specialNo || "",
            regNo: bill.regNo || "",
            patientName: bill.patientName || "",
            consultantName: bill.consultantName || "",
            bedNo: bill.bedNo || "",
            gender: bill.gender || "",
            age: bill.age || "",
            admissionDate: bill.admissionDate ? bill.admissionDate.split("T")[0] : "",
            billingDate: bill.billingDate ? bill.billingDate.split("T")[0] : new Date().toISOString().split("T")[0],
            refer: agent ? agent.label : bill.refer || "",
            referAmount: bill.referAmount || "",
            billNumber: bill.billNumber || "",
            authorizedBy: bill.authorizedBy || "",
          });
          
          setTableData(
            bill.serviceItems?.map((item) => ({
              serviceId: item.serviceName || "",
              particulars: item.particulars || "",
              providedBy: item.providedBy || "",
              unitPrice: item.unitPrice || 0,
              totalUnit: item.totalUnit || "",
              amount: item.amount || 0,
            })) || [{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]
          );
          setServiceCharge(bill.serviceCharge || "0");
          setServiceChargePc(bill.serviceChargePc || "0");
          setDiscount(bill.discount || "0");
          setDiscountBy(bill.discountBy || null);
          setPaidAmount(bill.paidAmount || "0");
          setBillResponse(bill);
          setBillExists(true);

        } catch (error) {
          console.error("Error loading bill:", error);
          toast.error("Failed to load bill data", { autoClose: 2000 });
        } finally {
          setIsLoading(false);
        }
      };
      loadBill();
    } else {
      handleReset();
      setSelectedBillId(null);
    }
  }
}, [isOpen, billId, initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "billNumber") {
      setBillNumberSearch(value);
      if (value) {
        const filteredOptions = allBills
          .filter((bill) =>
            bill.billNumber.toLowerCase().includes(value.toLowerCase())
          )
          .map((bill) => bill.billNumber);
        setBillNumberOptions(filteredOptions);
      } else {
        setBillNumberOptions(allBills.map((bill) => bill.billNumber));
      }
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (field) => async (selectedOption) => {
    if (field === "patientName" && selectedOption) {
      const selectedPatient = patients.find((p) => p.value === selectedOption.value);
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          patientName: selectedOption.label,
          specialNo: selectedPatient?.specialNo || "",
          regNo: selectedPatient?.regNo || "",
          admissionDate: selectedPatient?.admissionDate || "",
          consultantName: selectedPatient?.consultantName || prev.consultantName,
          refer: selectedPatient?.agentName || prev.refer,
          bedNo: selectedPatient?.bedNo || "",
          gender: selectedPatient?.sex || "",
          age: selectedPatient?.age || "",
        };
        console.log("Selected Patient Data:", { field, selectedOption, regNo: selectedPatient?.regNo, newFormData });
        return newFormData;
      });
      setErrors((prev) => ({ ...prev, patientName: "", specialNo: "", regNo: "", admissionDate: "" }));

      const advPhone = selectedPatient?.phoneNumber || "";
      const advPatientId = selectedPatient?.patientId || "";
      let foundAdvance = null;
      setAdvanceInfo(null);
      if (advPhone || advPatientId) {
        try {
          const advParams = new URLSearchParams();
          if (advPatientId) advParams.append("patientId", advPatientId);
          if (advPhone) advParams.append("phone", advPhone);
          const advResp = await axios.get(
            `${API_BASE_URL}/nicu/advance/search?${advParams.toString()}`,
            { headers: { "x-api-key": API_KEY } }
          );
          const totalAdvance = advResp.data.totalAdvance || 0;
          if (totalAdvance > 0) {
            foundAdvance = { advances: advResp.data.advances || [], totalAdvance, phone: advPhone };
          }
        } catch (advErr) {
          console.error("Advance lookup failed:", advErr);
        }
      }

      const regNo = selectedPatient?.regNo || "";
      if (regNo) {
        const exists = await checkExistingBill(regNo);
        setBillExists(exists);
        if (exists) {
          try {
            const bill = allBills.find((b) => b.regNo === regNo);
            if (bill) {
              const response = await axios.get(`https://api.muktihospital.com/api/nicu/nicu-bills/${bill.id}`, {
                headers: {
                  "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
                },
              });
              const fetchedBill = response.data.bill || response.data;

              // Load the whole bill — services and every total — just like opening it
              // from the Bill Number box. Setting the id puts the form in edit mode so
              // saving updates this bill instead of trying to create a duplicate.
              setSelectedBillId(fetchedBill.id);
              const agent = agents.find((a) => a.value === fetchedBill.refer);
              setFormData((prev) => ({
                ...prev,
                specialNo: fetchedBill.specialNo || "",
                regNo: fetchedBill.regNo || "",
                patientName: fetchedBill.patientName || "",
                admissionDate: fetchedBill.admissionDate ? fetchedBill.admissionDate.split("T")[0] : "",
                billingDate: fetchedBill.billingDate ? fetchedBill.billingDate.split("T")[0] : prev.billingDate,
                refer: agent ? agent.label : fetchedBill.refer || "",
                referAmount: fetchedBill.referAmount || "",
                billNumber: fetchedBill.billNumber || "",
                authorizedBy: fetchedBill.authorizedBy || "",
                consultantName: fetchedBill.consultantName || selectedPatient?.consultantName || "",
                bedNo: fetchedBill.bedNo || prev.bedNo || "",
                gender: fetchedBill.gender || prev.gender || "",
                age: fetchedBill.age || prev.age || "",
              }));
              setTableData(
                fetchedBill.serviceItems?.map((item) => ({
                  serviceId: item.serviceName || "",
                  particulars: item.particulars || "",
                  providedBy: item.providedBy || "",
                  unitPrice: item.unitPrice || 0,
                  totalUnit: item.totalUnit || "",
                  amount: item.amount || 0,
                })) || [{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]
              );
              setServiceCharge(fetchedBill.serviceCharge || "0");
              setServiceChargePc(fetchedBill.serviceChargePc || "0");
              setDiscount(fetchedBill.discount || "0");
              setDiscountBy(fetchedBill.discountBy || null);
              setPaidAmount(fetchedBill.paidAmount || "0");
              setBillResponse(fetchedBill);
              toast.info("This patient already has a bill — opened it for editing.", { autoClose: 2500 });
            }
          } catch (error) {
            console.error("Error loading bill for patient:", error);
            toast.error("Failed to load bill details.", { autoClose: 2000 });
          }
        } else {
          setSelectedBillId(null);
          setTableData([{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
          setServiceCharge("0");
          setServiceChargePc("0");
          setDiscount("0");
          setPaidAmount("0");
          setBillResponse({
            billAmount: 0,
            serviceChargePc: 0,
            serviceCharge: 0,
            totalAmount: 0,
            discountPc: 0,
            discount: 0,
            netAmount: 0,
            paidAmount: 0,
            payableAmount: 0,
          });
        }
      }

      if (foundAdvance) {
        setAdvanceInfo(foundAdvance);
        setPaidAmount(String(Math.round(foundAdvance.totalAdvance)));
        toast.info(`Advance ৳${Math.round(foundAdvance.totalAdvance)} applied to Paid.`, { autoClose: 3000 });
      }
    } else if (field === "refer" && selectedOption) {
      setFormData((prev) => ({
        ...prev,
        refer: selectedOption.label,
      }));
      setErrors((prev) => ({ ...prev, refer: "" }));
    } else if (field === "consultantName" && selectedOption) {
      setFormData((prev) => ({
        ...prev,
        consultantName: selectedOption.label,
      }));
      setErrors((prev) => ({ ...prev, consultantName: "" }));
    } else if (field === "authorizedBy" && selectedOption) {
      setFormData((prev) => ({
        ...prev,
        authorizedBy: selectedOption.label,
      }));
      setErrors((prev) => ({ ...prev, authorizedBy: "" }));
    }
  };

  const handleTableInputChange = (index, e) => {
  const { name, value } = e.target;
  const newTableData = [...tableData];
  newTableData[index] = { ...newTableData[index], [name]: value };
  
  if (name === "totalUnit") {
    const unitPrice = newTableData[index].unitPrice || 0;
    const totalUnit = parseFloat(value) || 0;
    newTableData[index].amount = Math.round(totalUnit * unitPrice);
    if (value && !isNaN(totalUnit) && totalUnit > 0) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newTableData.every((row) => row.totalUnit && parseFloat(row.totalUnit) > 0)) {
          delete newErrors.totalUnit;
        }
        return newErrors;
      });
    }
  }
  setTableData(newTableData);
};

  const handleServiceChange = (index) => (selectedOption) => {
    const serviceId = selectedOption ? selectedOption.value : "";
    const selectedService = services.find((service) => service.value === serviceId);

    if (
      selectedService &&
      tableData.some((r, i) => i !== index && r.serviceId === selectedService.label)
    ) {
      toast.error(`"${selectedService.label}" is already added. Increase its quantity instead.`);
      return;
    }
    const newTableData = [...tableData];
    newTableData[index] = {
      ...newTableData[index],
      serviceId: selectedService ? selectedService.label : "",
      particulars: selectedService?.particulars || "",
      providedBy: selectedService?.providedBy || "",
      unitPrice: selectedService?.unitPrice || 0,
      amount: Math.round(parseFloat(newTableData[index].totalUnit || 0) * (selectedService?.unitPrice || 0)),
    };
    setTableData(newTableData);
  };

  const handleAddRow = () => {
    const next = [...tableData, { serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }];
    setTableData(next);
    setTimeout(() => {
      const rows = serviceBodyRef.current?.querySelectorAll("tr");
      rows?.[next.length - 1]?.querySelector("input")?.focus();
    }, 60);
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.target.tagName === "TEXTAREA") return;
    if (e.defaultPrevented) return;
    e.preventDefault();

    if (["particulars", "providedBy", "unitPrice", "totalUnit"].includes(e.target.name)) {
      handleAddRow();
    }
  };

  const handleRemoveRow = (index) => {
    if (tableData.length > 1) {
      setTableData(tableData.filter((_, i) => i !== index));
    }
  };

  const handleOtherInputChange = (setter) => (e) => {
    setter(e.target.value);
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleReset = () => {
  setFormData({
    specialNo: "",
    regNo: "",
    patientName: "",
    admissionDate: "",
    billingDate: new Date().toISOString().split("T")[0],
    refer: "",
    referAmount: "",
    billNumber: "",
    authorizedBy: "", 
    consultantName: "",
    bedNo: "",
    gender: "",
    age: "",
  });
  setTableData([{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
  setServiceCharge("0");
  setServiceChargePc("0");
  setDiscount("0");
  setDiscountBy(null);
  setAdvanceInfo(null);
  setPaidAmount("0");
  setErrors({});
  setSelectedBillId(null);
  setBillResponse({
    billAmount: 0,
    serviceChargePc: 0,
    serviceCharge: 0,
    totalAmount: 0,
    discountPc: 0,
    discount: 0,
    netAmount: 0,
    paidAmount: 0,
    payableAmount: 0,
  });
  setBillNumberSearch("");
  setBillNumberOptions([]);
  setBillExists(false);
  setAllBills([]);
  axios
    .get("https://api.muktihospital.com/api/nicu/nicu-bills", {
      headers: {
        "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
      },
    })
    .then((response) => {
      const billsData = Array.isArray(response.data)
        ? response.data
        : response.data.bills || [];
      setAllBills(billsData);
      setBillNumberOptions(billsData.map((bill) => bill.billNumber));
    })
    .catch((error) => {
      console.error("Error fetching bills on reset:", error);
      toast.error("Failed to refresh bill data.", { autoClose: 2000 });
    });
};

 const handleAgentAdded = (savedAgent) => {
    const label = savedAgent?.agentName || savedAgent?.name || "";
    if (label) {
      setAgents((prev) => [{ value: label, label }, ...prev]);
      setFormData((prev) => ({ ...prev, refer: label }));
      toast.success(`Agent "${label}" added`, { autoClose: 2000 });
    }
    setShowAgentModal(false);
  };

  const checkExistingBill = async (regNo) => {
    try {
      const exists = allBills.some((bill) => bill.regNo === regNo);
      return exists;
    } catch (error) {
      console.error("Error checking existing bill:", error);
      toast.error("Unable to check existing bill. Proceeding without validation.", { autoClose: 2000 });
      return false;
    }
  };

  const handleSelectBillNumber = async (billNumber) => {
  if (!billNumber) return;

  setIsLoading(true);
  try {
    const bill = allBills.find((b) => b.billNumber === billNumber);
    if (!bill) {
      throw new Error("Bill not found");
    }

    const response = await axios.get(`https://api.muktihospital.com/api/nicu/nicu-bills/${bill.id}`, {
      headers: {
        "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
      },
    });
    const fetchedBill = response.data.bill || response.data;
    setSelectedBillId(fetchedBill.id);
    const agent = agents.find((a) => a.value === fetchedBill.refer);
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        specialNo: fetchedBill.specialNo || "",
        regNo: fetchedBill.regNo || "",
        patientName: fetchedBill.patientName || "",
        admissionDate: fetchedBill.admissionDate ? fetchedBill.admissionDate.split("T")[0] : "",
        billingDate: fetchedBill.billingDate ? fetchedBill.billingDate.split("T")[0] : prev.billingDate,
        refer: agent ? agent.label : fetchedBill.refer || "",
        referAmount: fetchedBill.referAmount || "",
        billNumber: fetchedBill.billNumber || "",
        authorizedBy: fetchedBill.authorizedBy || "",
        consultantName: fetchedBill.consultantName || "",
      };
      return newFormData;
    });
    setTableData(
      fetchedBill.serviceItems?.map((item) => ({
        serviceId: item.serviceName || "",
        particulars: item.particulars || "",
        providedBy: item.providedBy || "",
        unitPrice: item.unitPrice || 0,
        totalUnit: item.totalUnit || "",
        amount: item.amount || 0,
      })) || [{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]
    );
    setServiceCharge(fetchedBill.serviceCharge || "0");
    setServiceChargePc(fetchedBill.serviceChargePc || "0");
    setDiscount(fetchedBill.discount || "0");
    setPaidAmount(fetchedBill.paidAmount || "");
    setBillResponse(fetchedBill);
    setBillNumberSearch("");
    setBillNumberOptions([]);
    setErrors((prev) => ({ ...prev, billNumber: "" }));
    setBillExists(true);
  } catch (error) {
    console.error("Error loading bill by number:", error);
    setErrors((prev) => ({ ...prev, billNumber: "No bill found with this number" }));
    toast.error("No bill found with this number", { autoClose: 2000 });
  } finally {
    setIsLoading(false);
  }
};

const handleSubmit = async (e) => {
  if (e && e.preventDefault) e.preventDefault();
  const newErrors = {};
  if (!formData.authorizedBy) newErrors.authorizedBy = "Authorizer is required";
  if (!formData.specialNo) newErrors.specialNo = "Special No is required";
  if (!formData.regNo) newErrors.regNo = "Reg No is required";
  if (!formData.patientName) newErrors.patientName = "Patient name is required";
  if (!formData.admissionDate) newErrors.admissionDate = "Admission Date is required";
  if (!formData.billingDate) newErrors.billingDate = "Billing Date is required";
  if (tableData.some((row) => !row.serviceId)) newErrors.serviceId = "Service selection is required for all rows";
  if (tableData.some((row) => !row.totalUnit || parseFloat(row.totalUnit) <= 0)) {
    newErrors.totalUnit = "Total Unit must be a positive number for all rows";
  }
  if (!paidAmount) newErrors.paidAmount = "Paid amount is required";

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    toast.error("Please fill all required fields", { autoClose: 2000 });
    return;
  }

  setIsLoading(true);
  setProcessingAction('save');

    const payload = {
      specialNo: formData.specialNo,
      regNo: formData.regNo,
      admissionDate: formData.admissionDate,
      billingDate: formData.billingDate,
      refer: formData.refer,
      referAmount: parseFloat(formData.referAmount) || 0,
      serviceCharge: Math.round(billResponse?.serviceCharge) || 0,
      discount: parseFloat(discount) || 0,
      discountBy: discountBy || null,
      serviceChargePc: parseFloat(serviceChargePc) || 0, 
      discountPc: 0,
      paidAmount: parseFloat(paidAmount) || 0,
      patientName: formData.patientName,
      authorizedBy: formData.authorizedBy,
      consultantName: formData.consultantName,
      bedNo: formData.bedNo || null,
      gender: formData.gender || null,
      age: formData.age || null,
      serviceItems: tableData
        .filter(
          (row) =>
            row.serviceId &&
            row.unitPrice !== "" &&
            row.unitPrice !== undefined &&
            row.totalUnit !== "" &&
            row.totalUnit !== undefined
        )
        .map((row) => ({
          serviceName: row.serviceId,
          particulars: row.particulars,
          providedBy: row.providedBy,
          unitPrice: parseFloat(row.unitPrice) || 0,
          totalUnit: parseInt(row.totalUnit) || 0,
          amount: parseFloat(row.amount) || 0,
        })),
    };

  try {
    // console.log("Submitting Payload:", payload);

    const existingBill = await checkExistingBill(formData.regNo);
    if (existingBill && !selectedBillId) {
      toast.error("Bill for this registration number already exists! Please update the existing bill.", { autoClose: 2000 });
      return;
    }

    let response;
    if (selectedBillId) {
      response = await axios.put(
        `https://api.muktihospital.com/api/nicu/nicu-bills/edit/${selectedBillId}`,
        payload,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        }
      );
      toast.success("Bill updated successfully!", { autoClose: 2000 });
    } else {
      if (!payload.clientId) payload.clientId = generateClientId();
      response = await axios.post(
        "https://api.muktihospital.com/api/nicu/nicu-bills/generate",
        payload,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        }
      );
      toast.success("Bill saved successfully!", { autoClose: 2000 });
    }
    const savedBill = response.data.bill || response.data;
    onSave(savedBill, false);

    if (advanceInfo?.advances?.length > 0) {
      try {
        await axios.post(
          `${API_BASE_URL}/nicu/advance/use`,
          { ids: advanceInfo.advances.map((a) => a.id), billNumber: savedBill.billNumber || null },
          { headers: { "x-api-key": API_KEY } }
        );
      } catch (advErr) {
        console.error("Failed to mark advance as used:", advErr);
      }
      setAdvanceInfo(null);
    }

    if (!selectedBillId) {
      handleReset();
      toast.info("Ready for a new bill.", { autoClose: 1500 });
    } else {
      setBillResponse(savedBill);
      setFormData((prev) => ({ ...prev, billNumber: savedBill.billNumber || "" }));

      if (advanceInfo?.advances?.length > 0) {
      try {
        await axios.post(
          `${API_BASE_URL}/nicu/advance/use`,
          { ids: advanceInfo.advances.map((a) => a.id), billNumber: savedBill.billNumber || null },
          { headers: { "x-api-key": API_KEY } }
        );
      } catch (advErr) {
        console.error("Failed to mark advance as used:", advErr);
      }
      setAdvanceInfo(null);
    }
    }
    const billsResp = await axios.get("https://api.muktihospital.com/api/nicu/nicu-bills", {
      headers: {
        "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
      },
    });
    const billsData = Array.isArray(billsResp.data) ? billsResp.data : billsResp.data.bills || [];
    setAllBills(billsData);
    setBillNumberOptions(billsData.map((bill) => bill.billNumber));
  } catch (error) {
    console.error("Error saving/updating bill:", { message: error.message, response: error.response?.data });
    if ((error.code === "ERR_NETWORK" || !navigator.onLine) && !selectedBillId) {
      const clientId = payload.clientId || generateClientId();
      payload.clientId = clientId;
      const provisionalNo = `OFFLINE-${clientId.slice(0, 8)}`;
      const queued = await addToNicuOutbox("bill", payload, provisionalNo);
      if (queued) {
        await saveNicuLocal({
          clientId,
          type: "bill",
          provisionalNo,
          patientName: payload.patientName,
          regNo: payload.regNo,
          netAmount: payload.netAmount,
          paidAmount: payload.paidAmount,
          billingDate: payload.billingDate,
          pendingSync: true,
          createdAt: new Date().toISOString(),
        });
        toast.info(`Bill saved offline (${provisionalNo}). It will sync when you're back online.`, { autoClose: 3000 });
        handleReset();
      } else {
        toast.error("Could not save bill offline. Please try again.", { autoClose: 2000 });
      }
    } else {
      setErrors({
        apiError: error.response?.data?.error || "Failed to save/update bill",
      });
      toast.error("Failed to save/update bill", { autoClose: 2000 });
    }
  } finally {
    setIsLoading(false);
    setProcessingAction(null);
  }
};

const handleSaveAndPrint = async (e) => {
  e.preventDefault();
  const newErrors = {};
  if (!formData.specialNo) newErrors.specialNo = "Special No is required";
  if (!formData.regNo) newErrors.regNo = "Reg No is required";
  if (!formData.authorizedBy) newErrors.authorizedBy = "Authorizer is required";
  if (!formData.patientName) newErrors.patientName = "Patient name is required";
  if (!formData.admissionDate) newErrors.admissionDate = "Admission Date is required";
  if (!formData.billingDate) newErrors.billingDate = "Billing Date is required";
  if (tableData.some((row) => !row.serviceId)) newErrors.serviceId = "Service selection is required for all rows";
  if (tableData.some((row) => !row.totalUnit || parseFloat(row.totalUnit) <= 0)) {
    newErrors.totalUnit = "Total Unit must be a positive number for all rows";
  }
  if (!paidAmount) newErrors.paidAmount = "Paid amount is required";

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    toast.error("Please fill all required fields", { autoClose: 2000 });
    return;
  }

  setIsLoading(true);
  setProcessingAction('print');

    const payload = {
      specialNo: formData.specialNo,
      regNo: formData.regNo,
      admissionDate: formData.admissionDate,
      billingDate: formData.billingDate,
      billHeading: formData.billHeading,
      refer: formData.refer,
      referAmount: parseFloat(formData.referAmount) || 0,
      serviceCharge: Math.round(billResponse?.serviceCharge) || 0,
      discount: parseFloat(discount) || 0,
      discountBy: discountBy || null,
      serviceChargePc: parseFloat(serviceChargePc) || 0, 
      discountPc: 0,
      paidAmount: parseFloat(paidAmount) || 0,
      patientName: formData.patientName,
      authorizedBy: formData.authorizedBy,
      consultantName: formData.consultantName,
      bedNo: formData.bedNo || null,
      gender: formData.gender || null,
      age: formData.age || null,
      serviceItems: tableData
        .filter(
          (row) =>
            row.serviceId &&
            row.unitPrice !== "" &&
            row.unitPrice !== undefined &&
            row.totalUnit !== "" &&
            row.totalUnit !== undefined
        )
        .map((row) => ({
          serviceName: row.serviceId,
          particulars: row.particulars,
          providedBy: row.providedBy,
          unitPrice: parseFloat(row.unitPrice) || 0,
          totalUnit: parseInt(row.totalUnit) || 0,
          amount: parseFloat(row.amount) || 0,
        })),
    };

  try {

    console.log("🔵 PAYLOAD পাঠাচ্ছি:", JSON.stringify({
      bedNo: formData.bedNo,
      gender: formData.gender,
      age: formData.age,
      discountBy: discountBy,
    }));

    const existingBill = await checkExistingBill(formData.regNo);
    if (existingBill && !selectedBillId) {
      toast.error("Bill for this registration number already exists! Please update the existing bill.", { autoClose: 2000 });
      return;
    }

    let response;
    if (selectedBillId) {
      response = await axios.put(
        `https://api.muktihospital.com/api/nicu/nicu-bills/edit/${selectedBillId}`,
        payload,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        }
      );
      toast.success("Bill updated successfully!", { autoClose: 2000 });
    } else {
      // Attach a clientId so an offline save can sync idempotently later.
      if (!payload.clientId) payload.clientId = generateClientId();
      response = await axios.post(
        "https://api.muktihospital.com/api/nicu/nicu-bills/generate",
        payload,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        }
      );
      toast.success("Bill saved successfully!", { autoClose: 2000 });
    }

    const savedBill = response.data.bill || response.data;
    setBillResponse(savedBill);
    setFormData((prev) => ({ ...prev, billNumber: savedBill.billNumber || "" }));

    // Lock the advance to this bill so it can never be applied twice
    if (advanceInfo?.advances?.length > 0) {
      try {
        await axios.post(
          `${API_BASE_URL}/nicu/advance/use`,
          { ids: advanceInfo.advances.map((a) => a.id), billNumber: savedBill.billNumber || null },
          { headers: { "x-api-key": API_KEY } }
        );
      } catch (advErr) {
        console.error("Failed to mark advance as used:", advErr);
      }
      setAdvanceInfo(null);
    }

    let pathologyDue = 0;
    let pathologySlips = [];
    try {
      const pRes = await axios.get(
        `${API_BASE_URL}/pathology-bill/nicu-slip/list?regNo=${encodeURIComponent(formData.regNo || "")}`,
        { headers: { "x-api-key": API_KEY } }
      );
      pathologySlips = (pRes.data.bills || []).filter((b) => (b.dueAmount || 0) > 0);
      pathologyDue = pathologySlips.reduce((s, b) => s + (b.dueAmount || 0), 0);
    } catch (pErr) {
      console.error("Pathology due lookup failed:", pErr);
    }

    const signatureFooter = (
      <>
        <div className="np-sign-item">
          <div className="np-sign-space" />
          <div className="np-sign-line" />
          <p className="np-sign-label">Prepared By</p>
        </div>
        <div className="np-sign-item">
          <div className="np-sign-space" />
          <div className="np-sign-line" />
          <p className="np-sign-label">Authorized By</p>
        </div>
        <div className="np-sign-item">
          <div className="np-sign-space" />
          <div className="np-sign-line" />
          <p className="np-sign-label">Accounts Officer</p>
        </div>
      </>
    );

    const billInfo = (
      <div style={{ border: "1px solid #000", padding: "5px 8px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", columnGap: "14px", rowGap: "1px" }}>
          <p style={{ margin: 0 }}><strong>Bill No:</strong> {savedBill.billNumber || formData.billNumber || "N/A"}</p>
          <p style={{ margin: 0 }}><strong>Reg No:</strong> {formData.regNo || "N/A"}</p>
          <p style={{ margin: 0 }}><strong>Special No:</strong> {formData.specialNo || "N/A"}</p>

          <p style={{ margin: 0, gridColumn: "1 / 2" }}><strong>Patient:</strong> {formData.patientName || "N/A"}</p>
          <p style={{ margin: 0 }}><strong>Age / Sex:</strong> {formData.age || "--"} / {formData.gender || "--"}</p>
          <p style={{ margin: 0 }}><strong>Bed No:</strong> {formData.bedNo || "--"}</p>

          <p style={{ margin: 0 }}><strong>Admit Date:</strong> {formData.admissionDate ? new Date(formData.admissionDate).toLocaleDateString("en-GB") : "N/A"}</p>
          <p style={{ margin: 0 }}><strong>Bill Date:</strong> {formData.billingDate ? new Date(formData.billingDate).toLocaleDateString("en-GB") : "N/A"}</p>

          <p style={{ margin: 0, gridColumn: "1 / -1" }}><strong>Consultant:</strong> {formData.consultantName || "N/A"}</p>
        </div>
      </div>
    );

    // table = ONLY thead + tbody (NicuPrintLayout wraps it in <table>)
    const table = (
      <>
        <thead>
          <tr>
            <th style={{ width: "8%" }}>Sl</th>
            <th>Service Name</th>
            <th style={{ width: "16%" }}>Unit Price</th>
            <th style={{ width: "12%" }}>Qty</th>
            <th style={{ width: "18%" }}>Amount (৳)</th>
          </tr>
        </thead>
        <tbody>
          {tableData
            .filter((row) => row.serviceId || row.amount)
            .map((row, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{row.serviceId || "N/A"}{row.particulars ? ` — ${row.particulars}` : ""}</td>
                <td>{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
                <td>{row.totalUnit || "0"}</td>
                <td>{parseFloat(row.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
        </tbody>
      </>
    );

    const summary = (
      <>
        <div><span>Bill Amount</span><span>৳ {Math.round(billResponse?.billAmount || 0).toFixed(2)}</span></div>
        <div>
          <span>Service Charge{parseFloat(serviceChargePc) > 0 ? ` (${parseFloat(serviceChargePc)}%)` : ""}</span>
          <span>৳ {Math.round(billResponse?.serviceCharge || 0).toFixed(2)}</span>
        </div>
        <div><span>Discount</span><span>৳ {Math.round(billResponse?.discount || 0).toFixed(2)}</span></div>
        <div><span>Net Amount</span><span>৳ {Math.round(billResponse?.netAmount || 0).toFixed(2)}</span></div>
        <div><span>Paid Amount</span><span>৳ {Math.round(billResponse?.paidAmount || 0).toFixed(2)}</span></div>
        <div><span>Payable (Due)</span><span>৳ {Math.round(billResponse?.payableAmount || 0).toFixed(2)}</span></div>
      </>
    );

    // Pathology dues shown on the bill copy so the guardian knows what is still pending
    const note = pathologyDue > 0 ? (
      <div>
        <p style={{ margin: 0, fontWeight: "bold" }}>
          Pending Pathology Dues: ৳ {Math.round(pathologyDue).toFixed(2)}
        </p>
        <p style={{ margin: "4px 0 0" }}>
          {pathologySlips.map((b) => `${b.billNumber} (৳${Math.round(b.dueAmount)})`).join(", ")}
        </p>
        <p style={{ margin: "4px 0 0" }}>
          Please clear the pathology payment as well — release requires all bills to be paid.
        </p>
      </div>
    ) : null;

    const newPrintData = { billInfo, table, summary, signatureFooter, note };
    setPrintData(newPrintData);
     setShowPrintPreview(true);

     if (!selectedBillId) {
       handleReset();
     }

    const billsResp = await axios.get("https://api.muktihospital.com/api/nicu/nicu-bills", {
      headers: {
        "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
      },
    });
    const billsData = Array.isArray(billsResp.data) ? billsResp.data : billsResp.data.bills || [];
    setAllBills(billsData);
    setBillNumberOptions(billsData.map((bill) => bill.billNumber));

    setTimeout(async () => {
      const elementsToHide = document.querySelectorAll(".print-hidden, .print-hidden-in-print");
      elementsToHide.forEach((el) => (el.style.display = "none"));

      const printLayout = document.querySelector(".print-preview");
      if (printLayout) {
        printLayout.style.display = "block";
      }

      window.print();

      setTimeout(() => {
        elementsToHide.forEach((el) => (el.style.display = ""));
        if (printLayout) {
          printLayout.style.display = "none";
        }
        setShowPrintPreview(false);
        onSave(savedBill, true);
      }, 500);
    }, 1000);

  } catch (error) {
    console.error("Error saving/updating bill:", { message: error.message, response: error.response?.data });
    // Offline: queue NEW bills only. Edits and advance-use stay online-only
    // (they depend on server-side balances that can't be recomputed offline).
    if ((error.code === "ERR_NETWORK" || !navigator.onLine) && !selectedBillId) {
      const clientId = payload.clientId || generateClientId();
      payload.clientId = clientId;
      const provisionalNo = `OFFLINE-${clientId.slice(0, 8)}`;
      const queued = await addToNicuOutbox("bill", payload, provisionalNo);
      if (queued) {
        await saveNicuLocal({
          clientId,
          type: "bill",
          provisionalNo,
          patientName: payload.patientName,
          regNo: payload.regNo,
          netAmount: payload.netAmount,
          paidAmount: payload.paidAmount,
          billingDate: payload.billingDate,
          pendingSync: true,
          createdAt: new Date().toISOString(),
        });
        toast.info(`Bill saved offline (${provisionalNo}). It will sync when you're back online.`, { autoClose: 3000 });
        handleReset();
      } else {
        toast.error("Could not save bill offline. Please try again.", { autoClose: 2000 });
      }
    } else {
      setErrors({
        apiError: error.response?.data?.error || "Failed to save/update bill",
      });
      toast.error("Failed to save/update bill", { autoClose: 2000 });
    }
  } finally {
    setIsLoading(false);
    setProcessingAction(null);
  }
};

  if (!isOpen) return null;

const foundConsultant = consultants.find((c) => c.label === formData.consultantName);
console.log("3. Found Match in List?:", foundConsultant);

const calculatedValue = foundConsultant || (formData.consultantName ? { label: formData.consultantName, value: formData.consultantName } : null);
console.log("4. Final Value passed to Select:", calculatedValue);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white shadow-lg w-full max-w-full max-h-[100vh] h-full overflow-y-auto print-wrapper">
        <div className="bg-[#ececec] p-5 flex flex-col md:flex-row justify-between items-center mb-4 border-b border-dashed border-gray-300">
          <div className="flex flex-col items-center md:items-start">
            <h2 className="text-xl font-semibold text-gray-700">
              {selectedBillId ? "Edit NICU Bill" : "Generate NICU Bill"}
            </h2>
          </div>
          <div className="flex items-start justify-center flex-wrap gap-2 mt-3 xl:mt-0 relative z-50">
            <div className="flex items-center gap-2">
              <DynamicSelect
                options={patients}
                value={
                  formData.patientName
                    ? patients.find((option) => option.label === formData.patientName)
                    : null
                }
                onChange={handleSelectChange("patientName")}
                isSearchable
                placeholder="Search admitted Patient"
                className={`w-full rounded-md border ${
                  errors.patientName ? "border-red-500" : "border-gray-300"
                } bg-gray-50 shadow-sm`}
              />
              {errors.patientName && (
                <p className="text-red-500 text-xs mt-1.5">
                  {errors.patientName}
                </p>
              )}
            </div>
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                name="billNumber"
                value={formData.billNumber}
                onChange={handleInputChange}
                ref={searchRef}
                className={`w-64 p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.billNumber ? "border-red-500" : "border-gray-300"
                } bg-gray-50 shadow-sm`}
                placeholder="Search Bill Number"
                autoComplete="off"
              />
              {billNumberSearch && billNumberOptions.length > 0 && (
                <ul className="absolute top-full left-0 w-64 bg-white border border-gray-300 mt-1 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                  {billNumberOptions.map((billNumber, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectBillNumber(billNumber)}
                    >
                      {billNumber}
                    </li>
                  ))}
                </ul>
              )}   
              {errors.billNumber && (
                <p className="text-red-500 text-xs mt-1.5">
                  {errors.billNumber}
                </p>
              )}
            </div>
            <button
              title="Close"
              className="size-7 rounded-full bg-white text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
              onClick={onClose}
              disabled={isLoading}
            >
              <RiCloseLargeFill />
            </button>
          </div>
        </div>
        <div className="my-4 px-6 h-[calc(100vh-150px)] overflow-y-auto">
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div>
                <label className="label font-medium text-black">Special No</label>
                <input
                  type="text"
                  name="specialNo"
                  value={formData.specialNo}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.specialNo ? "border-red-500" : "border-gray-300"
                  } ${selectedBillId ? "bg-gray-100" : ""}`}
                  placeholder="Enter Special No"
                  disabled={!!selectedBillId}
                />
                {errors.specialNo && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.specialNo}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">Reg No</label>
                <input
                  type="text"
                  name="regNo"
                  value={formData.regNo}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.regNo ? "border-red-500" : "border-gray-300"
                  } ${selectedBillId ? "bg-gray-100" : ""}`}
                  placeholder="Enter Reg No (e.g., MH_PB_00001)"
                  disabled={!!selectedBillId}
                />
                {errors.regNo && (
                  <p className="text-red-500 text-xs mt-1.5">{errors.regNo}</p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Admission Date
                </label>
                <input
                  type="date"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.admissionDate ? "border-red-500" : "border-gray-300"
                  } ${selectedBillId ? "bg-gray-100" : ""}`}
                  disabled={!!selectedBillId}
                />
                {errors.admissionDate && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.admissionDate}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Billing Date
                </label>
                <input
                  type="date"
                  name="billingDate"
                  value={formData.billingDate}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.billingDate ? "border-red-500" : "border-gray-300"
                  } ${selectedBillId ? "bg-gray-100" : ""}`}
                  disabled={!!selectedBillId}
                />
                {errors.billingDate && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.billingDate}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">Refer</label>
                <DynamicSelect
                  options={agents}
                  value={
                    agents.find((option) => option.label === formData.refer) ||
                    (formData.refer ? { value: formData.refer, label: formData.refer } : null)
                  }
                  onChange={handleSelectChange("refer")}
                  isSearchable
                  placeholder="Select Refer"
                  className={`w-full rounded-md border ${
                    errors.refer ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.refer && (
                  <p className="text-red-500 text-xs mt-1.5">{errors.refer}</p>
                )}
                <button
                  type="button"
                  onClick={() => setShowAgentModal(true)}
                  className="mt-1 text-xs text-[#73CB64] hover:text-M-primary-color hover:underline font-medium"
                >
                  + Add Refer
                </button>
              </div>
              <div>
                <label className="label font-medium text-black">
                  Refer Amount
                </label>
                <input
                  type="number"
                  name="referAmount"
                  value={formData.referAmount}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.referAmount ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter refer amount"
                  min="0"
                />
                {errors.referAmount && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.referAmount}
                  </p>
                )}
              </div>
                 <div>
                <label className="label font-medium text-black">
                  Authorized By <span className="text-red-600">*</span>
                </label>
                <DynamicSelect
                  options={authorizedByOptions}
                  value={
                    formData.authorizedBy
                      ? authorizedByOptions.find(
                          (option) => option.value === formData.authorizedBy
                        )
                      : null
                  }
                  onChange={handleSelectChange("authorizedBy")}
                  isSearchable
                  placeholder="Select Authorizer"
                  className={`w-full rounded-md border ${
                    errors.authorizedBy ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.authorizedBy && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.authorizedBy}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Consultant Name
                </label>
                <DynamicSelect
                  options={consultants}
                  value={
                    consultants.find((c) => c.label === formData.consultantName) ||
                    (formData.consultantName
                      ? { label: formData.consultantName, value: formData.consultantName }
                      : null)
                  }
                  onChange={handleSelectChange("consultantName")}
                  isSearchable
                  placeholder="Select Consultant"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>
              <div>
                <label className="label font-medium text-black">Bed No</label>
                <input type="text" name="bedNo" value={formData.bedNo} readOnly
                  className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-100 focus:outline-none"
                  placeholder="Auto from admission" />
              </div>
              <div>
                <label className="label font-medium text-black">Gender</label>
                <input type="text" name="gender" value={formData.gender} readOnly
                  className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-100 focus:outline-none"
                  placeholder="Auto from admission" />
              </div>
              <div>
                <label className="label font-medium text-black">Age</label>
                <input type="text" name="age" value={formData.age} readOnly
                  className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-100 focus:outline-none"
                  placeholder="Auto from admission" />
              </div>
            </div>
            <div className="overflow-x-auto col-span-2 mt-6">
              <h2 className="text-lg font-bold text-gray-700 mb-3">Services</h2>
              <table className="w-full table-fixed border-collapse min-w-[1000px] mb-5">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Service Name <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Particulars
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Provided By
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Unit Price <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Total Unit <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Amount
                    </th>
                    <th className="text-center text-xs text-black border border-gray-300 py-2 px-3 w-16 font-medium">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody ref={serviceBodyRef}>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-gray-200 py-2 px-3">
                        <DynamicSelect
                          options={services.filter(
                  (opt) =>
                    opt.label === row.serviceId ||
                    !tableData.some((r, i) => i !== index && r.serviceId === opt.label)
                )}
                          value={
                            services.find(
                              (service) => service.label === row.serviceId
                            ) || null
                          }
                          onChange={handleServiceChange(index)}
                          placeholder="Service"
                          isSearchable
                          className={`w-full rounded-md border ${
                            errors.serviceId ? "border-red-500" : "border-gray-300"
                          } bg-gray-50 shadow-sm`}
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="text"
                          name="particulars"
                          value={row.particulars}
                          onChange={(e) => handleTableInputChange(index, e)} 
                          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Particulars"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="text"
                          name="providedBy"
                          value={row.providedBy}
                          onChange={(e) => handleTableInputChange(index, e)} 
                          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Provided By"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="unitPrice"
                          value={row.unitPrice}
                          onChange={(e) => handleTableInputChange(index, e)} 
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="totalUnit"
                          value={row.totalUnit}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.totalUnit ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="amount"
                          value={Math.round(row.amount)}
                          readOnly
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                          placeholder="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <button
                          type="button"
                          title="Remove"
                          className="size-7 mx-auto rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          onClick={() => handleRemoveRow(index)}
                          disabled={tableData.length === 1}
                        >
                          <RiDeleteBinLine />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Bill Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.billAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <button
                        type="button"
                        title="Add Row"
                        className="size-7 mx-auto rounded-full bg-[#73CB64] text-white text-sm flex items-center justify-center hover:bg-M-primary-color transition-all duration-300"
                        onClick={handleAddRow}
                      >
                        <RiAddFill />
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Service Charge (%)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="serviceChargePc"
                        value={serviceChargePc}
                        onChange={handleOtherInputChange(setServiceChargePc)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        {parseFloat(serviceChargePc) > 0
                          ? `${parseFloat(serviceChargePc)}% of ৳${Math.round(billResponse?.billAmount || 0)}`
                          : "Leave 0 to type a fixed amount below"}
                      </p>
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                   <td
                    className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                    colSpan={5}
                  >
                    Service Charge (৳)
                  </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                          type="number"
                          name="serviceCharge"
                          value={parseFloat(serviceChargePc) > 0 ? Math.round(billResponse?.serviceCharge || 0) : serviceCharge}
                          onChange={handleOtherInputChange(setServiceCharge)}
                          readOnly={parseFloat(serviceChargePc) > 0}
                          className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.serviceCharge ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      {errors.serviceChargePc && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.serviceChargePc}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Total Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.totalAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Discount By
                    </td>
                    <td className="border border-gray-200 py-2 px-3" colSpan={2}>
                      <DynamicSelect
                        options={discountUsers.map((u) => ({ value: u.userName, label: u.userName }))}
                        value={discountBy ? { value: discountBy, label: discountBy } : null}
                        onChange={(opt) => setDiscountBy(opt ? opt.value : null)}
                        isSearchable
                        placeholder="Select who gives discount"
                        isDisabled={!!selectedBillId}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium" colSpan={5}>
                      Discount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="discount"
                        value={discount}
                        onChange={handleOtherInputChange(setDiscount)}
                        readOnly={!!selectedBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.discount ? "border-red-500" : "border-gray-300"} ${selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.discount && (
                        <p className="text-red-500 text-xs mt-1.5">{errors.discount}</p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Net Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.netAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Paid (৳) <span className="text-red-600">*</span>
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="paidAmount"
                        value={paidAmount}
                        onChange={handleOtherInputChange(setPaidAmount)}
                        readOnly={!!selectedBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.paidAmount ? "border-red-500" : "border-gray-300"
                          }
                          ${selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.paidAmount && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.paidAmount}
                        </p>
                      )}
                      {advanceInfo && (
                        <p className="text-green-600 text-xs mt-1.5 font-medium">
                          Advance ৳{Math.round(advanceInfo.totalAdvance)} applied
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Payable (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.payableAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
        </div>
        <div className="flex justify-end items-center gap-3 px-6 fixed w-full bottom-0 p-3 bg-gray-200 z-20 border-t border-dashed border-M-text-color/50">
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="bg-gray-600 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
              disabled={isLoading}
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndPrint}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {processingAction === 'print' 
                ? (selectedBillId ? "Updating..." : "Saving...") 
                : (selectedBillId ? "Update & Print" : "Save & Print")
              }
            </button>
            <button
              onClick={handleSubmit}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {processingAction === 'save'
                ? (selectedBillId ? "Updating..." : "Saving...") 
                : (selectedBillId ? "Update" : "Save")
              }
            </button>
          </div>
        </div>
        {showPrintPreview && printData && (
          <div className="print-preview">
            <NicuPrintLayout
              className="print-preview"
              title="NICU Bill"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              footer={printData.signatureFooter}
              note={printData.note}
              isPaid={(billResponse?.payableAmount || 0) <= 0}
              compact
            />
          </div>
        )}
        <NicuAgentModal
          isOpen={showAgentModal}
          onClose={() => setShowAgentModal(false)}
          onSave={handleAgentAdded}
          agentToEdit={null}
        />
      </div>
    </div>
  );
};

export default NicuBillModal;
